import {SerializedTableNode, TableNode} from '@lexical/table';
import {
  $applyNodeReplacement,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  NodeKey,
  Spread,
} from 'lexical';

export type SerializedEnhancedTableNode = Spread<
  {
    addRowsDisabled?: boolean;
    addColumnsDisabled?: boolean;
  },
  SerializedTableNode
>;

export class EnhancedTableNode extends TableNode {
  __addRowsDisabled: boolean;
  __addColumnsDisabled: boolean;

  static getType(): string {
    return 'table';
  }

  static clone(node: EnhancedTableNode): EnhancedTableNode {
    const cloned = new EnhancedTableNode(
      node.__addRowsDisabled,
      node.__addColumnsDisabled,
      node.__key
    );
    // The parent TableNode's properties will be copied by Lexical's internal cloning
    return cloned;
  }

  constructor(
    addRowsDisabled: boolean = false,
    addColumnsDisabled: boolean = false,
    key?: NodeKey
  ) {
    super(key);
    this.__addRowsDisabled = addRowsDisabled;
    this.__addColumnsDisabled = addColumnsDisabled;
  }

  // Getters and setters
  setAddRowsDisabled(addRowsDisabled: boolean): void {
    const writableNode = this.getWritable();
    writableNode.__addRowsDisabled = addRowsDisabled;
  }

  getAddRowsDisabled(): boolean {
    return this.getLatest().__addRowsDisabled;
  }

  // Helper to check if adding rows is enabled (for convenience)
  canAddRows(): boolean {
    return !this.getAddRowsDisabled();
  }

  setAddColumnsDisabled(addColumnsDisabled: boolean): void {
    const writableNode = this.getWritable();
    writableNode.__addColumnsDisabled = addColumnsDisabled;
  }

  getAddColumnsDisabled(): boolean {
    return this.getLatest().__addColumnsDisabled;
  }

  // Helper to check if adding columns is enabled (for convenience)
  canAddColumns(): boolean {
    return !this.getAddColumnsDisabled();
  }

  createDOM(config: EditorConfig): HTMLElement {
    // Call parent to get the scrollable wrapper with the table inside
    const scrollableWrapper = super.createDOM(config);

    // Find the table element inside
    const table = scrollableWrapper.querySelector('table');
    if (table) {
      // Create shadow wrapper for the shadow effect
      const shadowWrapper = document.createElement('div');
      shadowWrapper.setAttribute('data-table-shadow-wrapper', 'true');

      // Create left shadow element (sticky to left edge)
      const leftShadow = document.createElement('div');
      leftShadow.setAttribute('data-table-shadow', 'left');

      // Create right shadow element (sticky to right edge)
      const rightShadow = document.createElement('div');
      rightShadow.setAttribute('data-table-shadow', 'right');

      // Move the table into the shadow wrapper
      table.parentNode?.insertBefore(shadowWrapper, table);
      shadowWrapper.appendChild(leftShadow);
      shadowWrapper.appendChild(table);
      shadowWrapper.appendChild(rightShadow);

      // Create spacer div after the shadow wrapper for button/scrollbar space
      const spacer = document.createElement('div');
      spacer.setAttribute('data-table-spacer', 'true');
      scrollableWrapper.appendChild(spacer);

      // Update shadow visibility based on scroll position
      const updateShadows = () => {
        const {scrollLeft, scrollWidth, clientWidth} = scrollableWrapper;
        const canScrollLeft = scrollLeft > 0;
        const canScrollRight = scrollLeft + clientWidth < scrollWidth - 1; // -1 for rounding

        leftShadow.style.opacity = canScrollLeft ? '1' : '0';
        rightShadow.style.opacity = canScrollRight ? '1' : '0';
      };

      // Initial update and listen for scroll
      updateShadows();
      scrollableWrapper.addEventListener('scroll', updateShadows);

      // Also update on resize
      const resizeObserver = new ResizeObserver(updateShadows);
      resizeObserver.observe(scrollableWrapper);
      resizeObserver.observe(table);
    }

    return scrollableWrapper;
  }

  exportDOM(): DOMExportOutput {
    // Create a plain table element for export without needing the active editor context.
    // The shadow wrappers and scroll handlers are only needed for the live editor DOM.
    const element = document.createElement('table');
    return {element};
  }

  static importDOM(): DOMConversionMap | null {
    // We inherit from TableNode, so we can use its importDOM
    // But we override it to create EnhancedTableNode instead
    return {
      table: () => ({
        conversion: $convertTableElement,
        priority: 1,
      }),
    };
  }

  exportJSON(): SerializedEnhancedTableNode {
    return {
      ...super.exportJSON(),
      type: 'table',
      addRowsDisabled: this.__addRowsDisabled,
      addColumnsDisabled: this.__addColumnsDisabled,
    };
  }

  static importJSON(serializedNode: SerializedEnhancedTableNode): EnhancedTableNode {
    const {addRowsDisabled = false, addColumnsDisabled = false} = serializedNode;
    const node = $createEnhancedTableNode(addRowsDisabled, addColumnsDisabled);
    return node;
  }
}

export function $createEnhancedTableNode(
  addRowsDisabled: boolean = false,
  addColumnsDisabled: boolean = false
): EnhancedTableNode {
  return $applyNodeReplacement(
    new EnhancedTableNode(addRowsDisabled, addColumnsDisabled)
  ) as EnhancedTableNode;
}

export function $isEnhancedTableNode(node: unknown): node is EnhancedTableNode {
  return node instanceof EnhancedTableNode;
}

// Helper to convert regular TableNode to EnhancedTableNode
export function $convertToEnhancedTableNode(
  tableNode: TableNode,
  addRowsDisabled: boolean = false,
  addColumnsDisabled: boolean = false
): EnhancedTableNode {
  const enhancedNode = $createEnhancedTableNode(addRowsDisabled, addColumnsDisabled);

  // Copy all children
  const children = tableNode.getChildren();
  children.forEach((child) => {
    enhancedNode.append(child);
  });

  return enhancedNode;
}

// Convert pasted table elements to EnhancedTableNode
function $convertTableElement(): DOMConversionOutput {
  return {node: $createEnhancedTableNode()};
}
